import type { StatusBadgeTone } from "@louise/moon-ui";
import type { TranslationFunction } from "@src/packages/i18n";
import { EMBRYO_RESULT_LABEL_KEYS, EMBRYO_RESULT_TONES } from "./embryoResult";
import { parseLocalDate } from "@src/packages/utils/date";
import type { EmbryoStorageState } from "./embryoStorage";
import type { EmbryoEntry, EmbryoResultReason } from "./embryoTestingOverview";

/** A report or vault date in the patient's locale, e.g. "August 2, 2026". */
export function formatEmbryoDate(
  value: string | null | undefined,
  locale: string,
): string | null {
  if (!value) return null;
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? parseLocalDate(value)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}

/** One line for where an embryo is now, e.g. "Frozen at Northlake Fertility". */
export function describeEmbryoStorage(
  storage: EmbryoStorageState,
  t: TranslationFunction,
  locale: string,
): string {
  switch (storage.kind) {
    case "frozen":
      return storage.facility
        ? t("care.embryo_report.storage_status.frozen_at_facility", {
            params: { facility: storage.facility },
          })
        : t("care.embryo_report.storage_status.frozen");
    case "transferred": {
      const date = formatEmbryoDate(storage.date, locale);
      return date
        ? t("care.embryo_report.storage_status.transferred_on", {
            params: { date },
          })
        : t("care.embryo_report.storage_status.transferred");
    }
    case "thawed": {
      const date = formatEmbryoDate(storage.date, locale);
      return date
        ? t("care.embryo_report.storage_status.thawed_on", { params: { date } })
        : t("care.embryo_report.storage_status.thawed");
    }
    case "discarded":
      return t("care.embryo_report.storage_status.discarded");
    case "donated":
      return t("care.embryo_report.storage_status.donated");
    case "in_transit":
      return t("care.embryo_report.storage_status.moving_between_clinics");
  }
}

/** The finding behind a result, e.g. "Extra copy of chromosome 21". */
export function describeEmbryoResultReason(
  reason: EmbryoResultReason,
  t: TranslationFunction,
): string {
  switch (reason.kind) {
    case "extra_copy":
      return t("care.embryo_report.result_reason.extra_copy_of_chromosome", {
        params: { chromosome: reason.chromosome },
      });
    case "missing_copy":
      return t("care.embryo_report.result_reason.missing_copy_of_chromosome", {
        params: { chromosome: reason.chromosome },
      });
    case "changes_on":
      return t("care.embryo_report.result_reason.changes_on_chromosomes", {
        params: { chromosomes: reason.chromosomes.join(", ") },
      });
    case "mosaic":
      return t("care.embryo_report.result_reason.mosaic_share_of_cells", {
        params: { chromosome: reason.chromosome, level: String(reason.level) },
      });
    case "mosaic_on":
      return t("care.embryo_report.result_reason.mosaic_on_chromosomes", {
        params: { chromosomes: reason.chromosomes.join(", ") },
      });
    case "no_result":
      return t("care.embryo_report.result_reason.biopsy_gave_no_result");
  }
}

/** How an embryo reads in a list, e.g. "Embryo 2" or "Untested embryo 1". */
export function describeEmbryoEntryName(
  entry: EmbryoEntry,
  t: TranslationFunction,
): string {
  switch (entry.kind) {
    case "tested":
      return t("care.embryo_report.embryo_number", {
        params: { number: String(entry.embryo.embryoNumber) },
      });
    case "awaiting":
      return t("care.embryo_report.biopsied_embryo_number", {
        params: { number: String(entry.number) },
      });
    case "untested":
      return t("care.embryo_report.untested_embryo_number", {
        params: { number: String(entry.number) },
      });
  }
}

/** The badge beside an embryo: its result, or why it has none. */
export function describeEmbryoEntryStatus(
  entry: EmbryoEntry,
  t: TranslationFunction,
): { label: string; tone: StatusBadgeTone } {
  switch (entry.kind) {
    case "tested":
      return {
        label: t(EMBRYO_RESULT_LABEL_KEYS[entry.embryo.result]),
        tone: EMBRYO_RESULT_TONES[entry.embryo.result],
      };
    case "awaiting":
      return {
        label: t("care.embryo_report.awaiting_result"),
        tone: "neutral",
      };
    case "untested":
      return { label: t("care.embryo_report.not_tested"), tone: "neutral" };
  }
}
